import { useEffect } from 'react';
import { RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { BrandingProvider } from '@/providers/BrandingProvider';
import { IntlProvider } from '@/i18n';
import { ToastProvider } from '@/components/ui/Toast';
import { router } from '@/router';
import { useAuthStore } from '@/stores/useAuthStore';
import { setupTokenRefreshInterceptor } from '@/services/apiInterceptors';
import { queryClient } from '@/lib/queryClient';
import { streamManager } from '@/stores/streamManager';

/**
 * Root application component.
 *
 * Wraps the app with providers:
 * 1. QueryClientProvider -- TanStack Query for server state
 * 2. IntlProvider -- i18n with react-intl (PT-BR default)
 * 3. ThemeProvider -- Dark/light/system theme switching (Zustand-backed)
 * 4. BrandingProvider -- Fetches public branding, applies theme colors + title + favicon
 * 5. ToastProvider -- Toast notifications
 * 6. RouterProvider -- React Router v7
 *
 * On mount, hydrates auth state from localStorage and sets up
 * the token refresh interceptor.
 */

// Set up token refresh interceptor once at module level
setupTokenRefreshInterceptor();

// Attach the singleton QueryClient to the stream manager so it can
// read/write the TanStack Query cache outside the React tree.
streamManager.attachQueryClient(queryClient);

function AuthHydrator({ children }: { children: React.ReactNode }) {
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider>
        <ThemeProvider>
          <BrandingProvider>
            <ToastProvider>
              <AuthHydrator>
                <RouterProvider router={router} />
              </AuthHydrator>
            </ToastProvider>
          </BrandingProvider>
        </ThemeProvider>
      </IntlProvider>
    </QueryClientProvider>
  );
}
